// ============================================================
// TMR Portal Admin — receiving-account reads and writes, over @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api, ApiError } from '@tmr/api-client';
import { companyKeys, financeKeys } from '../queryClient';
import type {
  AddReceivingAccountRequest,
  Bank,
  BankAccountDetailsInput,
  CurrentAccount,
  ReceivingAccount,
} from './contract';

/**
 * An Empresa's receiving accounts, default first.
 *
 * A 404 here means "unknown Empresa **or** outside your scope" — the backend refuses to tell the two
 * apart on purpose (06 §6). A 403 means the operator lacks `finance.read`, which is a different screen
 * state: the data exists and is not theirs to see.
 *
 * @param companyId The Empresa whose accounts to read, or undefined while the route param resolves.
 */
export function useReceivingAccounts(
  companyId: string | undefined,
): UseQueryResult<ReceivingAccount[]> {
  return useQuery({
    queryKey: companyKeys.receivingAccounts(companyId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ReceivingAccount[]>(`/companies/${companyId}/receiving-accounts`, { signal }),
    enabled: Boolean(companyId),
  });
}

/**
 * The account money goes to today, or null when the Empresa has none.
 *
 * Onboarding provisions one from the wizard's bank block, but only eventually — the Finance module
 * creates it while reacting to the onboarding event (13 §3), so a record read moments after creation
 * can legitimately have no account yet. Imported Empresas may never have had one.
 *
 * @param accounts The list as {@link useReceivingAccounts} returned it, or undefined while loading.
 */
export function defaultAccount(accounts: ReceivingAccount[] | undefined): ReceivingAccount | null {
  return accounts?.find((account) => account.isDefault) ?? null;
}

/** Adds a receiving account to an Empresa. */
export function useAddReceivingAccount(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddReceivingAccountRequest) =>
      api.post<ReceivingAccount>(`/companies/${companyId}/receiving-accounts`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.receivingAccounts(companyId) });
    },
  });
}

/**
 * Replaces one account's bank details.
 *
 * Whether it is the default is deliberately not part of this — that is its own action on the server, so
 * that "correct a typo in the branch number" can never move where the money goes as a side effect.
 */
export function useUpdateReceivingAccount(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, details }: { accountId: string; details: BankAccountDetailsInput }) =>
      api.put<ReceivingAccount>(
        `/companies/${companyId}/receiving-accounts/${accountId}`,
        details,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.receivingAccounts(companyId) });
    },
  });
}

/**
 * Makes one account the Empresa's default — where the money actually goes.
 *
 * Its own endpoint and its own call, for the reason spelled out above: the backend keeps "exactly one
 * default" with a partial unique index and moves both rows in one ordered transaction, so this is a
 * decision, never a by-product of an edit. The Conta tab is the first screen to reach it; before it, the
 * only default an Empresa could have was the one onboarding created.
 */
export function useSetDefaultReceivingAccount(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) =>
      api.post<ReceivingAccount>(
        `/companies/${companyId}/receiving-accounts/${accountId}/default`,
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.receivingAccounts(companyId) });
    },
  });
}

/**
 * The banks a receiving account can name, read from the legacy's own dictionary.
 *
 * Cached for a long window: it is a few hundred rows that change about never, and it is read by every
 * account form. An environment with no legacy is served the well-known banks rather than an empty select —
 * a form that cannot offer a bank cannot register an account at all.
 */
export function useBanks(): UseQueryResult<Bank[]> {
  return useQuery({
    queryKey: financeKeys.banks,
    queryFn: ({ signal }) => api.get<Bank[]>('/banks', { signal }),
    staleTime: 60 * 60_000,
  });
}

/**
 * The Empresa's conta corrente, or `null` when it has none yet.
 *
 * **A 404 is mapped to `null` on purpose.** The backend has no account to return before the Empresa's
 * contract is sent for signature, and that is a normal state the screen renders ("conta não aberta"), not
 * a failure. Leaving it as an error would put a retry button in front of something no retry can fix.
 * Every other status still throws, so a 403 remains the distinct "sem permissão" state.
 *
 * The same 404 also covers "Empresa outside your scope" (06 §6), which the tab would word wrongly. It
 * cannot happen in practice: the tab only renders inside a company detail whose own read already
 * succeeded, and that read is scoped the same way.
 *
 * @param companyId The Empresa whose account to read, or undefined while the route param resolves.
 */
export function useCurrentAccount(
  companyId: string | undefined,
): UseQueryResult<CurrentAccount | null> {
  return useQuery({
    queryKey: companyKeys.currentAccount(companyId ?? ''),
    queryFn: async ({ signal }) => {
      try {
        return await api.get<CurrentAccount>(`/companies/${companyId}/account`, { signal });
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: Boolean(companyId),
    // The withdrawal lock opens when the contract is signed, which happens at the provider (`09` §11, N2).
    // Both of this tab's headline facts — whether the account exists and whether it can withdraw — are
    // decided elsewhere, so returning to the tab is exactly when it is worth re-reading.
    refetchOnWindowFocus: true,
  });
}
